import { useEffect } from 'react';
import type { LoggerDraft } from './state/loggerDraft';
import type { LoggerHostDeps } from './hostDeps';

// Mirror the live draft into Sentry's activeWorkout context so any crash
// mid-workout carries enough metadata to repro: startedAt, entry slugs,
// set + completion counts, loadedSides distribution. No PII — exercise
// names / notes / RPE stay client-side. Cleared on unmount / hide so a
// post-save dismissal stops attaching stale state to unrelated crashes.
// Extracted from useLoggerScreen (size budget).
//
// RW-18: `sentryWorkoutContext` is optional — mobile injects its real
// Sentry-backed implementation (apps/mobile/src/util/crashReporting.ts,
// unchanged); web-rnw omits it (its WebCrashBoundary still catches a
// render crash, just without this breadcrumb).
export function useActiveWorkoutSentryContext(
  draft: LoggerDraft,
  visible: boolean,
  sentryWorkoutContext: LoggerHostDeps['sentryWorkoutContext'],
) {
  useEffect(() => {
    if (!visible || !sentryWorkoutContext) return undefined;
    sentryWorkoutContext.set(draft);
    return () => {
      // Breadcrumb BEFORE clearing. Sentry JACKEDLOGS-REACTNATIVE-11 (fatal
      // RetryableMountingLayerException, 4.13.1) fires during the post-save
      // transition out of the logger — i.e. after this cleanup has already
      // run — so both recorded events arrived with `activeWorkout` empty and
      // nothing to say what had just been torn down.
      //
      // The clear itself stays: attaching a stale workout to a LATER,
      // unrelated crash is the failure this cleanup was written to prevent.
      // A breadcrumb doesn't reintroduce that, because it is timestamped —
      // it says "the tree tore down HERE" instead of asserting a workout is
      // still live. That also separates the React teardown from the
      // `workoutFgs stop` breadcrumb, which is the notification service and
      // a different subsystem.
      sentryWorkoutContext.breadcrumb('logger.activeWorkoutContext.clear', {
        entryCount: draft.entries.length,
      });
      sentryWorkoutContext.clear();
    };
  }, [draft, visible, sentryWorkoutContext]);
}
